import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LuArrowLeft, LuArrowRight, LuCheck } from 'react-icons/lu';
import { technologyCategories } from './Technologies';

const technologyDetails = {
  frontend: {
    title: 'Frontend Engineering',
    subtitle: 'Building Modern User Experiences',
    heroDescription: 'We craft responsive, intuitive, and visually stunning frontend applications that deliver exceptional user experiences across all devices and platforms.',
    overview: 'Our frontend engineering team specializes in building high-performance web applications using the latest frameworks and best practices. We focus on creating seamless user interfaces that are not only beautiful but also accessible, fast, and maintainable.',
    capabilities: [
      'Single Page Applications (SPAs)',
      'Progressive Web Apps (PWAs)',
      'Server-Side Rendering (SSR)',
      'Static Site Generation (SSG)',
      'Responsive & Adaptive Design',
      'Cross-browser Compatibility',
      'Performance Optimization',
      'Accessibility (WCAG) Compliance'
    ],
    benefits: [
      { title: 'Faster Load Times', description: 'Optimized code and lazy loading for quick page loads' },
      { title: 'Better SEO', description: 'Search engine optimized architecture and meta management' },
      { title: 'Enhanced UX', description: 'Intuitive interfaces that delight users' },
      { title: 'Scalable Architecture', description: 'Component-based design for easy maintenance' }
    ]
  },
  backend: {
    title: 'Backend Engineering',
    subtitle: 'Powering Your Applications',
    heroDescription: 'We develop robust, scalable backend systems that form the foundation of your digital infrastructure with security, performance, and reliability at the core.',
    overview: 'Our backend engineering expertise spans across multiple languages and frameworks. We build microservices architectures, RESTful APIs, and real-time systems that can handle millions of requests while maintaining data integrity and security.',
    capabilities: [
      'RESTful & GraphQL APIs',
      'Microservices Architecture',
      'Real-time Systems (WebSockets)',
      'Message Queues & Event-driven Architecture',
      'Authentication & Authorization',
      'Database Design & Optimization',
      'Caching Strategies',
      'API Gateway & Load Balancing'
    ],
    benefits: [
      { title: 'High Performance', description: 'Optimized systems handling millions of requests' },
      { title: 'Scalability', description: 'Horizontal scaling with microservices' },
      { title: 'Security', description: 'Enterprise-grade security implementations' },
      { title: 'Reliability', description: '99.9% uptime with fault-tolerant design' }
    ]
  },
  genai: {
    title: 'Generative AI',
    subtitle: 'Transforming Business with AI',
    heroDescription: 'We leverage cutting-edge Generative AI technologies to automate processes, enhance decision-making, and create innovative solutions for your business.',
    overview: 'Our AI engineering team specializes in implementing Large Language Models (LLMs), building AI-powered applications, and creating intelligent automation solutions. We help businesses harness the power of AI to drive innovation and efficiency.',
    capabilities: [
      'LLM Integration & Fine-tuning',
      'Custom AI Model Development',
      'AI-powered Chatbots & Assistants',
      'Natural Language Processing (NLP)',
      'Computer Vision Solutions',
      'AI Workflow Automation',
      'RAG (Retrieval-Augmented Generation)',
      'AI Agent Development'
    ],
    benefits: [
      { title: 'Automation', description: 'Automate repetitive tasks with intelligent AI' },
      { title: 'Insights', description: 'Extract valuable insights from unstructured data' },
      { title: 'Efficiency', description: 'Reduce operational costs with AI optimization' },
      { title: 'Innovation', description: 'Create new products and services powered by AI' }
    ]
  },
  databases: {
    title: 'Databases',
    subtitle: 'Secure & Scalable Data Solutions',
    heroDescription: 'We implement optimal database solutions that ensure data integrity, high performance, and seamless scalability for your applications.',
    overview: 'Our database expertise covers both SQL and NoSQL solutions. We design schemas, optimize queries, implement sharding strategies, and ensure your data layer can handle your business growth while maintaining security and compliance.',
    capabilities: [
      'Database Architecture Design',
      'Query Optimization',
      'Data Migration & ETL',
      'Replication & Sharding',
      'Backup & Disaster Recovery',
      'Database Security & Encryption',
      'Performance Monitoring',
      'Multi-database Integration'
    ],
    benefits: [
      { title: 'Performance', description: 'Optimized queries for sub-millisecond responses' },
      { title: 'Scalability', description: 'Handle petabytes of data with ease' },
      { title: 'Reliability', description: 'Zero data loss with robust backup systems' },
      { title: 'Security', description: 'Encrypted data at rest and in transit' }
    ]
  },
  quality: {
    title: 'Quality Engineering',
    subtitle: 'Ensuring Flawless Delivery',
    heroDescription: 'We provide comprehensive quality engineering services covering automated testing, performance testing, and security assessments.',
    overview: 'Our QA team ensures your products meet the highest quality standards. From unit tests to end-to-end automation, load testing to security audits, we implement testing strategies that catch issues before they reach production.',
    capabilities: [
      'Test Automation Frameworks',
      'CI/CD Pipeline Integration',
      'Performance & Load Testing',
      'Security Testing & Audits',
      'Mobile App Testing',
      'API Testing',
      'Cross-browser Testing',
      'Accessibility Testing'
    ],
    benefits: [
      { title: 'Quality Assurance', description: 'Comprehensive test coverage for reliability' },
      { title: 'Faster Releases', description: 'Automated testing speeds up deployment' },
      { title: 'Cost Reduction', description: 'Catch bugs early before they become expensive' },
      { title: 'Confidence', description: 'Deploy with confidence knowing code is tested' }
    ]
  },
  digital: {
    title: 'Digital Experience',
    subtitle: 'Engaging Digital Journeys',
    heroDescription: 'We create seamless digital experiences and commerce solutions that engage customers and drive conversions across all touchpoints.',
    overview: 'Our digital experience team combines UX design, content management, and e-commerce expertise to create compelling digital journeys. We build platforms that not only look great but also convert visitors into loyal customers.',
    capabilities: [
      'CMS Implementation',
      'E-commerce Platforms',
      'UX/UI Design',
      'Digital Asset Management',
      'Personalization Engines',
      'Analytics Integration',
      'A/B Testing',
      'Conversion Optimization'
    ],
    benefits: [
      { title: 'Engagement', description: 'Higher user engagement with intuitive design' },
      { title: 'Conversions', description: 'Optimized funnels for better conversion rates' },
      { title: 'Brand Consistency', description: 'Unified experience across all channels' },
      { title: 'Data-driven', description: 'Analytics-powered optimization' }
    ]
  },
  mobile: {
    title: 'Mobile Development',
    subtitle: 'Apps That Users Love',
    heroDescription: 'We build high-performance native and cross-platform mobile applications that deliver exceptional user experiences on iOS and Android.',
    overview: 'Our mobile development team creates apps that combine beautiful design with powerful functionality. Whether native or cross-platform, we ensure your mobile presence is fast, reliable, and engaging.',
    capabilities: [
      'Native iOS Development',
      'Native Android Development',
      'Cross-platform Development',
      'Mobile UI/UX Design',
      'Push Notifications',
      'Offline-first Architecture',
      'App Store Optimization',
      'Mobile Analytics'
    ],
    benefits: [
      { title: 'Performance', description: 'Native-like performance across platforms' },
      { title: 'User Experience', description: 'Intuitive interfaces users love' },
      { title: 'Reach', description: 'Engage users on their preferred devices' },
      { title: 'Retention', description: 'Features that keep users coming back' }
    ]
  },
  cloud: {
    title: 'Cloud & DevOps',
    subtitle: 'Infrastructure for Scale',
    heroDescription: 'We streamline development and operations with cloud-native solutions, CI/CD pipelines, and infrastructure automation.',
    overview: 'Our cloud and DevOps expertise helps organizations modernize their infrastructure. We implement containerization, orchestration, and continuous delivery pipelines that accelerate development while maintaining reliability.',
    capabilities: [
      'Cloud Architecture Design',
      'Container Orchestration',
      'CI/CD Pipeline Implementation',
      'Infrastructure as Code',
      'Monitoring & Observability',
      'Security & Compliance',
      'Cost Optimization',
      'Disaster Recovery'
    ],
    benefits: [
      { title: 'Agility', description: 'Faster deployments with automation' },
      { title: 'Scalability', description: 'Auto-scaling based on demand' },
      { title: 'Cost Efficiency', description: 'Optimized cloud spending' },
      { title: 'Reliability', description: 'High availability and disaster recovery' }
    ]
  },
  data: {
    title: 'Data Engineering',
    subtitle: 'Insights from Your Data',
    heroDescription: 'We build data pipelines and analytics platforms that transform raw data into actionable insights for better business decisions.',
    overview: 'Our data engineering team specializes in building robust data infrastructure. From ETL pipelines to data warehouses, we help organizations manage, process, and analyze data at scale.',
    capabilities: [
      'Data Pipeline Development',
      'Data Warehouse Design',
      'ETL/ELT Processes',
      'Real-time Data Streaming',
      'Data Lake Architecture',
      'Business Intelligence',
      'Data Governance',
      'ML Pipeline Integration'
    ],
    benefits: [
      { title: 'Insights', description: 'Transform data into actionable insights' },
      { title: 'Efficiency', description: 'Automated data processing pipelines' },
      { title: 'Scale', description: 'Handle petabytes of data efficiently' },
      { title: 'Quality', description: 'Clean, reliable data for decisions' }
    ]
  },
  platform: {
    title: 'Platform Solutions',
    subtitle: 'Enterprise Platform Expertise',
    heroDescription: 'We provide expert implementation and customization services for enterprise platforms like ServiceNow, Salesforce, and Microsoft Power Platform.',
    overview: 'Our platform specialists help organizations maximize their investment in enterprise software. We implement, customize, and integrate platforms to streamline operations and drive digital transformation.',
    capabilities: [
      'Platform Implementation',
      'Custom Application Development',
      'Integration Services',
      'Workflow Automation',
      'Reporting & Dashboards',
      'User Training',
      'Platform Migration',
      'Support & Maintenance'
    ],
    benefits: [
      { title: 'Efficiency', description: 'Streamlined operations with automation' },
      { title: 'Integration', description: 'Connected systems across the enterprise' },
      { title: 'Visibility', description: 'Real-time insights with dashboards' },
      { title: 'ROI', description: 'Maximize platform investment returns' }
    ]
  }
};

export default function TechnologyDetail() {
  const { id } = useParams();
  const category = technologyCategories.find(cat => cat.id === id);
  const details = technologyDetails[id];
  
  if (!category || !details) {
    return (
      <div className="bg-slate-950 min-h-screen pt-32 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Technology Not Found</h1>
          <Link to="/technologies" className="text-orange-400 hover:text-orange-300">
            Back to Technologies
          </Link>
        </div>
      </div>
    );
  }

  const Icon = category.icon;
  const currentIndex = technologyCategories.findIndex(cat => cat.id === id);
  const prevCategory = technologyCategories[currentIndex - 1];
  const nextCategory = technologyCategories[currentIndex + 1];

  return (
    <div className="bg-slate-950 min-h-screen" data-testid="technology-detail-page">
      <section className="pt-32 pb-16 relative overflow-hidden" data-testid="tech-detail-hero">
        <div className="absolute inset-0">
          <motion.div
            animate={{ scale: [1, 1.2, 1], x: [0, 30, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 left-10 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ scale: [1, 1.3, 1], x: [0, -30, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-3xl"
          />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <Link 
            to="/technologies" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-orange-400 mb-8 transition-colors"
            data-testid="back-to-technologies"
          >
            <LuArrowLeft size={20} />
            Back to Technologies
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-xl bg-orange-500/20 border border-orange-500/50 flex items-center justify-center">
                <Icon size={32} className="text-orange-400" />
              </div>
              <div>
                <span className="text-orange-400 text-sm font-medium uppercase tracking-wider">{details.subtitle}</span>
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white">
                  {details.title}
                </h1>
              </div>
            </div>
            
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl">
              {details.heroDescription}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 border-t border-slate-800" data-testid="tech-detail-overview">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-white mb-6">Overview</h2>
              <p className="text-slate-300 leading-relaxed mb-8">{details.overview}</p>
              
              <h3 className="font-heading text-xl font-semibold text-white mb-4">Our Capabilities</h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {details.capabilities.map((capability, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-300">
                    <LuCheck className="text-orange-400 flex-shrink-0" size={18} />
                    <span className="text-sm">{capability}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-white mb-6">Technologies</h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                {category.technologies.map((tech, index) => (
                  <motion.div
                    key={tech.name}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex flex-col items-center group"
                  >
                    <div className="w-16 h-16 bg-slate-800 rounded-xl flex items-center justify-center p-2 group-hover:bg-slate-700 transition-all border border-slate-700 group-hover:border-orange-500/50">
                      <img 
                        src={tech.logo} 
                        alt={tech.name}
                        className="w-10 h-10 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<span class="text-lg font-bold text-slate-400">${tech.name.charAt(0)}</span>`;
                        }}
                      />
                    </div>
                    <span className="mt-2 text-xs text-slate-400 text-center font-medium">{tech.name}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-900/50" data-testid="tech-detail-benefits">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Why Choose Us for <span className="text-gradient-orange">{details.title}</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {details.benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-orange-500/50 transition-all"
              >
                <h3 className="font-heading text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-slate-800" data-testid="tech-detail-nav">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
            {prevCategory ? (
              <Link 
                to={`/technologies/${prevCategory.id}`}
                className="flex items-center gap-3 text-slate-400 hover:text-orange-400 transition-colors group"
              >
                <LuArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                <div>
                  <span className="text-xs uppercase tracking-wider">Previous</span>
                  <p className="font-heading font-semibold text-white group-hover:text-orange-400">{prevCategory.name}</p>
                </div>
              </Link>
            ) : <div />}
            
            {nextCategory ? (
              <Link 
                to={`/technologies/${nextCategory.id}`}
                className="flex items-center gap-3 text-slate-400 hover:text-orange-400 transition-colors group text-right"
              >
                <div>
                  <span className="text-xs uppercase tracking-wider">Next</span>
                  <p className="font-heading font-semibold text-white group-hover:text-orange-400">{nextCategory.name}</p>
                </div>
                <LuArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-b from-slate-900/30 via-orange-950/10 to-slate-900/30" data-testid="tech-detail-cta">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center space-y-6"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Ready to Build with {details.title}?
            </h2>
            <p className="text-slate-300">
              Let's discuss how our expertise can help bring your vision to life
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-10 py-4 rounded-full text-lg font-semibold transition-all shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 group"
            >
              <span>Get Started</span>
              <LuArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
